import { useState } from 'react';
import { supabase } from './supabaseClient';
import { getTheme, setTheme } from './theme';
import DeleteAccountSection from './DeleteAccountSection';

export default function ProfileEdit({ user, profile, onComplete, onCancel }) {
  const [username, setUsername] = useState(profile.username || '');
  const [contactEmail, setContactEmail] = useState(profile.contact_email || '');
  const [isPublic, setIsPublic] = useState(profile.is_public !== false);
  const [theme, setThemeState] = useState(getTheme());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Applies instantly (body class + localStorage) so the user previews the
  // theme live; SAVE/CANCEL don't need to touch it.
  const chooseTheme = (t) => { setTheme(t); setThemeState(t); };

  const handleSave = async () => {
    if (!username.trim()) { setError('Username is required.'); return; }
    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
      setError('Username can only contain letters, numbers, hyphens and underscores.');
      return;
    }
    setLoading(true);
    setError(null);
    const { error: err } = await supabase.from('profiles').update({
      username: username.trim().toLowerCase(),
      contact_email: contactEmail.trim(),
      is_public: isPublic,
      updated_at: new Date().toISOString(),
    }).eq('id', user.id);

    if (err) {
      if (err.code === '23505') setError('That username is already taken.');
      else setError(err.message);
      setLoading(false);
    } else {
      onComplete();
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.stars} />
      <div style={styles.card}>
        <div style={styles.logoLine}>COLLECTIONKEEPER</div>
        <h1 style={styles.title}><img src='/logo.png' style={{width:'52px',height:'52px',borderRadius:'10px',display:'block',margin:'0 auto 16px'}} alt='CK' />
        EDIT <span style={styles.accent}>PROFILE</span></h1>
        <div style={styles.divider} />

        <div style={styles.field}>
          <label style={styles.label}>USERNAME</label>
          <input style={styles.input} value={username} onChange={e => setUsername(e.target.value)} maxLength={30} />
          <div style={styles.hint}>Public profile: /u/{username || 'yourname'}</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>CONTACT EMAIL (private)</label>
          <input style={styles.input} type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="your@email.com" />
          <div style={styles.hint}>Never shown on your profile. Used only for Marketplace sale emails.</div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>PROFILE VISIBILITY</label>
          <div style={styles.toggleRow}>
            <button style={isPublic ? { ...styles.toggleBtn, ...styles.toggleBtnActive } : styles.toggleBtn} onClick={() => setIsPublic(true)}>PUBLIC</button>
            <button style={!isPublic ? { ...styles.toggleBtn, ...styles.toggleBtnActiveRed } : styles.toggleBtn} onClick={() => setIsPublic(false)}>PRIVATE</button>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>APPEARANCE</label>
          <div style={styles.toggleRow}>
            <button style={theme !== 'light' ? { ...styles.toggleBtn, ...styles.toggleBtnActiveRed } : styles.toggleBtn} onClick={() => chooseTheme('dark')}>⬛ DARK SIDE</button>
            <button style={theme === 'light' ? { ...styles.toggleBtn, ...styles.toggleBtnActiveCyan } : styles.toggleBtn} onClick={() => chooseTheme('light')}>⬜ LIGHT SIDE</button>
          </div>
          <div style={styles.appearanceHint}>
            {theme === 'light'
              ? 'Light Side is a bright, high-contrast theme with larger text for improved readability and accessibility. Your card colors stay the same — just tuned to read clearly on white. Applies to checklist pages for now.'
              : 'Dark Side is the classic deep-space look. Choose Light Side for a bright, high-contrast theme with larger text — easier to read, especially on phones. Applies to checklist pages for now.'}
          </div>
        </div>

        {error && <div style={styles.msgError}>{error}</div>}

        <div style={styles.btnRow}>
          <button style={styles.btnSecondary} onClick={onCancel}>CANCEL</button>
          <button style={styles.btn} onClick={handleSave} disabled={loading}>{loading ? 'SAVING...' : 'SAVE CHANGES'}</button>
        </div>

        <DeleteAccountSection />
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Rajdhani', sans-serif", position: 'relative' },
  stars: { position: 'fixed', inset: 0, opacity: 'var(--stars-opacity)', backgroundImage: `radial-gradient(1px 1px at 10% 20%, rgba(255,255,255,0.6) 0%, transparent 100%), radial-gradient(1.5px 1.5px at 50% 10%, rgba(255,255,255,0.7) 0%, transparent 100%)`, pointerEvents: 'none' },
  card: { position: 'relative', width: '100%', maxWidth: '480px', background: 'var(--panel-deep)', border: '1px solid var(--border)', borderRadius: '4px', padding: '40px', boxShadow: '0 0 60px rgba(var(--cyan-rgb),0.06)' },
  logoLine: { fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '6px', color: 'var(--brand-gold)', textAlign: 'center', marginBottom: '12px', opacity: 0.8 },
  title: { fontFamily: "'Orbitron', monospace", fontSize: '24px', fontWeight: 900, textAlign: 'center', color: 'var(--text-strong)', lineHeight: 1.1, margin: 0 },
  accent: { color: 'var(--gold)' },
  divider: { height: '1px', background: 'linear-gradient(90deg, transparent, var(--border), var(--brand-gold), var(--border), transparent)', margin: '24px 0' },
  field: { marginBottom: '20px' },
  label: { display: 'block', fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--muted-2)', marginBottom: '6px' },
  input: { width: '100%', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', fontFamily: "'Share Tech Mono', monospace", fontSize: '14px', padding: '10px 14px', borderRadius: '2px', outline: 'none', boxSizing: 'border-box' },
  hint: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-hint)', color: 'var(--muted-2)', marginTop: '6px' },
  appearanceHint: { fontFamily: "'Share Tech Mono', monospace", fontSize: 'var(--fs-hint)', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 },
  toggleRow: { display: 'flex', gap: '8px' },
  toggleBtn: { flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '9px', letterSpacing: '1px', padding: '9px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted-2)', cursor: 'pointer', borderRadius: '2px' },
  toggleBtnActive: { borderColor: 'var(--green)', color: 'var(--green)', background: 'rgba(var(--green-rgb),0.05)' },
  toggleBtnActiveRed: { borderColor: 'var(--red)', color: 'var(--red)', background: 'rgba(var(--red-rgb),0.05)' },
  toggleBtnActiveCyan: { borderColor: 'var(--cyan)', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.08)' },
  btnRow: { display: 'flex', gap: '8px', marginTop: '8px' },
  btn: { flex: 2, fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '2px', padding: '13px', background: 'linear-gradient(135deg, rgba(var(--cyan-rgb),0.15), rgba(201,162,39,0.1))', border: '1px solid var(--cyan)', color: 'var(--cyan)', cursor: 'pointer', borderRadius: '2px' },
  btnSecondary: { flex: 1, fontFamily: "'Orbitron', monospace", fontSize: '11px', letterSpacing: '2px', padding: '13px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted-2)', cursor: 'pointer', borderRadius: '2px' },
  msgError: { background: 'rgba(var(--red-rgb),0.1)', border: '1px solid rgba(var(--red-rgb),0.3)', color: 'var(--red)', fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', padding: '10px 14px', borderRadius: '2px', marginBottom: '12px', lineHeight: 1.5 },
};
